import { ExportButton } from '@ui/shared/Button';
import * as React from 'react';
import styled from 'styled-components';
import { IComparisonPopulationSetting } from '../../../../data/providers/settings/comparisonPopulationSettingsProvider';
import {
  IAggregate,
  IAggregated,
  ICombinedComputedAggregate,
  Level,
} from '../../../../data/types';
import { formatDay } from '../../../../utils/date';
import { Column } from '../../../layout/Column';
import { Row } from '../../../layout/Row';
import { ComparisonSettingsPopup } from '../../../settings/comparison/ComparisonSettingsPopup';
import { ZScoreSettings } from '../../../settings/ZScoreSettings';
import { CardHeader } from '../../../shared/card/CardHeader';
import { SearchInput } from '../../../shared/form/SearchInput';
import { ListHeader } from '../../../shared/List/ListHeader';

interface IProps {
  periodAggregates: IAggregated;
  listShape?: number[];
  settings: IComparisonPopulationSetting;
  loading: boolean;
  level: Level;
  filter?: string;
  onFilterChange: (val: string) => void;
  isPdf?: boolean;
  openExport: () => void;
  setLoading: (v: boolean) => void;
}
export const ScoringHeader = (props: IProps): React.ReactElement => {
  const comparisonLabel = React.useMemo((): string => {
    if (props.loading) return null;
    const aggregates = props?.periodAggregates?.aggregate;

    const count = findCountFromAggregates(aggregates?.data) ?? 0;

    const comparisonRangeDateLabel =
      props.settings?.dateRangeType?.value === 'custom'
        ? `${formatDay(
            props?.settings?.dateRangeValues?.from?.value
          )} - ${formatDay(props?.settings?.dateRangeValues?.to?.value)}`
        : props?.settings?.dateRangeType?.label;

    return ` ${props?.settings?.entity?.label} ${comparisonRangeDateLabel} (${count}) Tests`;
  }, [
    props.loading,
    JSON.stringify(props.settings),
    JSON.stringify(props.periodAggregates),
  ]);

  return (
    <CardHeader withListHeader>
      <Column>
        <Row style={{ paddingLeft: '10px', paddingRight: '10px' }}>
          <Column>
            <ComparisonPopulationDescription>
              Comparing to: {comparisonLabel}
            </ComparisonPopulationDescription>
            <SearchContainer data-html2canvas-ignore='true'>
              {!props.isPdf && (
                <SearchInput
                  style={{ marginLeft: '0px' }}
                  value={props.filter}
                  onChange={props.onFilterChange}
                  onClear={(): void => props.onFilterChange('')}
                />
              )}
            </SearchContainer>
          </Column>
          {!props.isPdf && (
            <OptionsContainer data-html2canvas-ignore='true'>
              <ExportButton
                onClick={props.openExport}
                />

              <ComparisonSettingsPopup
                level={props.level}
                setLoading={props.setLoading}
              />
            </OptionsContainer>
          )}
        </Row>
        <ListHeader
          style={{ marginTop: '0px' }}
          labels={['Metric', 'Mean', 'Change', 'ZScore', '']}
          shape={props.listShape}
        />
      </Column>
    </CardHeader>
  );
};

export const ZScoreLabel = (): React.ReactElement => {
  return (
    <ZScoreLabelContainer>
      <ZScoreLabelText>ZScore</ZScoreLabelText>
      <ZScoreSettings />
    </ZScoreLabelContainer>
  );
};

const ZScoreLabelContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const ZScoreLabelText = styled.div`
  margin-right: 3px;
`;

const ComparisonPopulationDescription = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 5px;
  color: var(--color-secondary-text);
`;

const SearchContainer = styled.div`
  margin-bottom: 10px;
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const OptionsContainer = styled(Column as any)`
  align-items: flex-end;
  padding-bottom: 5px;

  display: ${({ isPdf }: { isPdf: boolean }): string =>
    isPdf ? 'none' : 'flex'};
`;

/**
 * @description finds the total count of tests from a given combined aggregate
 * @param aggregates
 */
const findCountFromAggregates = (
  aggregates: ICombinedComputedAggregate
): number => {
  return Object.keys(aggregates ?? {}).reduce(
    (max: number, curr: string): number => {
      const count = (aggregates[curr] as IAggregate)?.count;
      return count > max ? count : max;
    },
    0
  );
};
